import apiClient from "./api.client.service";
import { AxiosError } from "axios";
import { ATSJobPost } from "../Interface";

interface ConnectedATSApp {
  appType: string;
  _id: string;
  name: string;
}

interface ServiceResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: unknown;
}

class ATSIntegrationService {
  private readonly APIBaseUrl: string;

  constructor() {
    this.APIBaseUrl = process.env.REACT_APP_API_BASE_URL ?? "";
  }


  async getConnectedATSApps(): Promise<ServiceResponse<ConnectedATSApp[]>> {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/app-integration/connected-apps`
      );
      const apps = response.data?.data || [];
      
      const mappedApps: ConnectedATSApp[] = apps
        .filter((app: { appType?: string; _id?: string; name?: string }) => 
          app.appType && app._id
        )
        .map((app: { appType: string; _id: string; name?: string }) => ({
          appType: app.appType,
          _id: app._id,
          name: app.name || app.appType,
        }));
      
      return {
        success: true,
        data: mappedApps,
      };
    } catch (error) {
      let errorMessage = 'Unknown error occurred';
      if (error instanceof AxiosError) {
        errorMessage = error.response?.data?.message || error.message || 'Failed to fetch connected ATS apps';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      console.error(
        "🚀 ~ ATSIntegrationService ~ getConnectedATSApps ~ error:",
        error
      );
      
      return {
        success: false,
        data: [],
        message: errorMessage,
        error,
      };
    }
  }

  async getATSJobPosts(
    appType: string,
    search?: string,
    sortBy?: 'title' | 'latest'
  ): Promise<ServiceResponse<ATSJobPost[]>> {
    try {
      let tempJobPosts: ATSJobPost[] = [];

      if (appType === 'lever') {
        const response = await apiClient.get(`${this.APIBaseUrl}/ats/lever/job-posts`);
        tempJobPosts = Array.isArray(response.data) 
          ? response.data 
          : (response.data?.data || []);
      } else if (appType === 'zoho') {
        const response = await apiClient.get(`${this.APIBaseUrl}/ats/zoho/job-posts`);
        tempJobPosts = Array.isArray(response.data) 
          ? response.data 
          : (response.data?.data || []);
      }

      const validatedPosts: ATSJobPost[] = tempJobPosts
        .filter((post): post is ATSJobPost => 
          post && 
          typeof post.jobPostId === 'string' &&
          typeof post.jobPostTitle === 'string' &&
          typeof post.description === 'string' &&
          typeof post.isSynced === 'boolean'
        )
        .map((post) => ({
          jobPostId: post.jobPostId,
          jobPostTitle: post.jobPostTitle,
          description: post.description,
          isSynced: post.isSynced,
        }));

      // Apply search filter
      let filteredPosts: ATSJobPost[] = validatedPosts;
      if (search?.trim()) {
        const searchLower = search.toLowerCase();
        filteredPosts = validatedPosts.filter(
          (post) =>
            post.jobPostTitle.toLowerCase().includes(searchLower) ||
            post.description.toLowerCase().includes(searchLower)
        );
      }

      // Apply sorting
      if (sortBy === 'title') {
        filteredPosts = [...filteredPosts].sort((a, b) =>
          a.jobPostTitle.localeCompare(b.jobPostTitle)
        );
      }

      return {
        success: true,
        data: filteredPosts,
      };
    } catch (error) {
      let errorMessage = 'Unknown error occurred';
      if (error instanceof AxiosError) {
        errorMessage = error.response?.data?.message || error.message || 'Failed to fetch ATS job posts';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      console.error(
        "🚀 ~ ATSIntegrationService ~ getATSJobPosts ~ error:",
        error
      );
      
      return {
        success: false,
        data: [],
        message: errorMessage,
        error,
      };
    }
  }

  // // Helper method to check if app has active job posts
  // async hasActiveJobPosts(appType: string): Promise<boolean> {
  //   try {
  //     const response = await this.getATSJobPosts(appType);
  //     if (!response.success || !response.data) {
  //       return false;
  //     }
  //     return response.data.some((post) => post.status === 'active');
  //   } catch (error) {
  //     console.error(
  //       "🚀 ~ ATSIntegrationService ~ hasActiveJobPosts ~ error:",
  //       error
  //     );
  //     return false;
  //   }
  // }

  // TODO: Replace with actual API call when backend is ready
  // POST /api/ats-integration/import-job-post
  async importATSJobPost(
    appType: string,
    jobPostId: string
  ): Promise<ServiceResponse<{ jobPostId: string; appType: string }>> {
    try {
      // TODO: Uncomment when API is ready
      // const response = await apiClient.post(
      //   `${this.APIBaseUrl}/ats-integration/import-job-post`,
      //   {
      //     appType,
      //     jobPostId,
      //   }
      // );
      // return {
      //   success: true,
      //   data: response.data,
      //   message: response.data?.message || 'Job post imported successfully',
      // };

      // Temporary response - Remove when API is ready
      return {
        success: true,
        data: { jobPostId, appType },
        message: 'Job post imported successfully',
      };
    } catch (error) {
      let errorMessage = 'Unknown error occurred';
      if (error instanceof AxiosError) {
        errorMessage = error.response?.data?.message || error.message || 'Failed to import job post';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      console.error(
        "🚀 ~ ATSIntegrationService ~ importATSJobPost ~ error:",
        error
      );
      
      return {
        success: false,
        message: errorMessage,
        error,
      };
    }
  }
}

const atsIntegrationService = new ATSIntegrationService();
export default atsIntegrationService;

